import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface TimePeriod {
  id: number;
  name: string;
  startYear: number;
  startQuarter: number;
  endYear: number;
  endQuarter: number;
}

interface Props {
  data: { categories: string[]; series: { name: string; data: (number | null)[] }[] };
  title?: string;
  neighborhoodName?: string;
  propertyTypeLabel?: string;
  timePeriods?: TimePeriod[];
}

const FONT = "'Montserrat Light', 'Montserrat', sans-serif";
const FONT_SIZE = '11pt';
const FONT_STYLE = { fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: 'normal' as const };

export const PriceIndexChart = React.forwardRef<HighchartsReact.RefObject, Props>(
  ({ data, title = 'Price Index', neighborhoodName = '', propertyTypeLabel = '', timePeriods = [] }, ref) => {
    const yearsInCategories = data.categories
      .map(c => parseInt(c.substring(0, 4), 10))
      .filter(y => !isNaN(y));
    const startYear = yearsInCategories.length > 0 ? Math.min(...yearsInCategories) : '';
    const endYear   = yearsInCategories.length > 0 ? Math.max(...yearsInCategories) : '';
    const subtitle  = `${neighborhoodName} | ${propertyTypeLabel} Price Appreciation ${startYear} – ${endYear}`;
    const hasMortgagePercent = data.series.some(s => s.name === 'Mortgage %');
    const hasMortgageRate    = data.series.some(s => s.name === '30yr Rate');
    const hasAnyOverlay      = hasMortgagePercent || hasMortgageRate;

    const catToNum = (yr: number, q: number) => yr * 4 + q;
    const firstCatNum = data.categories.length > 0 ? catToNum(parseInt(data.categories[0], 10), parseInt(data.categories[0].substring(6), 10)) : 0;
    const lastCatNum  = data.categories.length > 0 ? catToNum(parseInt(data.categories[data.categories.length - 1], 10), parseInt(data.categories[data.categories.length - 1].substring(6), 10)) : 0;

    const plotBands: Highcharts.XAxisPlotBandsOptions[] = timePeriods.flatMap(tp => {
      const fromNum = catToNum(tp.startYear, tp.startQuarter);
      const toNum   = catToNum(tp.endYear,   tp.endQuarter);
      if (toNum < firstCatNum || fromNum > lastCatNum) return [];
      const from = fromNum <= firstCatNum ? 0 : data.categories.indexOf(`${tp.startYear} Q${tp.startQuarter}`);
      const to   = toNum   >= lastCatNum  ? data.categories.length - 1 : data.categories.indexOf(`${tp.endYear} Q${tp.endQuarter}`);
      return [{
        color: 'rgba(173, 216, 230, 0.3)',
        from: from === -1 ? 0 : from,
        to:   to   === -1 ? data.categories.length - 1 : to,
        zIndex: 0,
        label: {
          text: tp.name,
          align: 'center' as const,
          verticalAlign: 'top' as const,
          y: -25,
          style: { fontSize: '9pt', color: '#555', fontFamily: FONT },
        },
      }];
    });

    const dataLabelFormatter: Highcharts.DataLabelsFormatterCallbackFunction = function () {
      const cat = data.categories[this.index!];
      return cat && cat.endsWith('Q1') ? Highcharts.numberFormat(this.y!, 1) : '';
    };

    // Highcharts' automatic label thinning counts from the left, which can drop
    // the last category or crowd it next to its neighbor. Anchor spacing at the
    // right edge instead, stepping backward, so the last label is always evenly spaced.
    const tickPositioner: Highcharts.AxisTickPositionerCallbackFunction = function (min, max) {
      const len = data.categories.length;
      if (len === 0) return [];
      const approxLabelWidth = 55;
      const maxLabels = Math.max(2, Math.floor(this.len / approxLabelWidth));
      const step = Math.max(1, Math.ceil((len - 1) / (maxLabels - 1)));
      const lo = Math.max(0, Math.floor(min));
      const hi = Math.min(len - 1, Math.ceil(max));
      const positions: number[] = [];
      for (let i = hi; i >= lo; i -= step) positions.push(i);
      positions.reverse();
      return positions;
    };

    const options: Highcharts.Options = {
      chart: {
        type: 'line',
        height: 560,
        marginTop: 160,
        style: { fontFamily: FONT, fontSize: FONT_SIZE }
      },
      title: {
        text: title,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: '500', color: '#000000' }
      },
      subtitle: {
        text: subtitle,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: '500', color: '#66635F' }
      },
      xAxis: {
        categories: data.categories,
        plotBands,
        title: { text: undefined },
        tickPositioner,
        labels: {
          rotation: -45,
          style: { ...FONT_STYLE, fontSize: '9pt' }
        }
      },
      yAxis: [
        {
          title: { text: undefined },
          labels: { style: FONT_STYLE },
          plotLines: [{ value: 100, color: '#cccccc', width: 1, dashStyle: 'Dash' }]
        },
        {
          title: { text: undefined },
          labels: { style: FONT_STYLE, formatter: function () { return `${this.value}%`; } },
          opposite: true,
          min: 0,
          max: 100,
          visible: hasMortgagePercent
        },
        {
          title: { text: undefined },
          labels: { style: FONT_STYLE, formatter: function () { return `${this.value}%`; } },
          opposite: true,
          min: 0,
          max: 10,
          visible: hasMortgageRate
        }
      ],
      legend: {
        enabled: hasAnyOverlay,
        itemStyle: FONT_STYLE
      },
      tooltip: {
        shared: true,
        style: FONT_STYLE,
        formatter: function () {
          return this.points!.map(p => {
            const isIndex = p.series.name === 'Price Index';
            const isRate  = p.series.name === '30yr Rate';
            const val = isIndex
              ? Highcharts.numberFormat(p.y!, 2)
              : `${Highcharts.numberFormat(p.y!, isRate ? 2 : 1)}%`;
            return `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${val}</b>`;
          }).join('<br/>');
        }
      },
      plotOptions: {
        line: {
          marker: { enabled: true, radius: 3 },
          connectNulls: false
        }
      },
      series: data.series.map(s => {
        const isMortgagePercent = s.name === 'Mortgage %';
        const isMortgageRate    = s.name === '30yr Rate';
        const isOverlay         = isMortgagePercent || isMortgageRate;
        return {
          type: 'line' as const,
          name: s.name,
          data: s.data,
          color: isMortgagePercent ? '#CBA968' : isMortgageRate ? '#4A8C5C' : '#738FAF',
          yAxis: isMortgagePercent ? 1 : isMortgageRate ? 2 : 0,
          marker: { enabled: true, radius: isOverlay ? 2 : 3 },
          dataLabels: isOverlay ? { enabled: false } : {
            enabled: true,
            allowOverlap: false,
            formatter: dataLabelFormatter,
            style: {
              fontFamily: FONT,
              fontSize: '9pt',
              fontWeight: 'normal',
              textOutline: 'none',
              color: '#000000'
            }
          }
        };
      }),
      credits: { enabled: false }
    };

    return <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />;
  }
);
